"use client"

// src/components/pages/ChatListPage.jsx
import { useEffect, useState } from "react"
import DashboardTemplate from "../templates/DashboardTemplate"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"
import { useNavigate } from "react-router-dom"
import Card from "../molecules/Card"

export default function ChatListPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [rooms, setRooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    api
      .get(`/chat/rooms?owner=${user._id}`)
      .then((res) => {
        setRooms(res.data)
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setError("Failed to load chat rooms. Please try again.")
        setLoading(false)
      })
  }, [user])

  return (
    <DashboardTemplate>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Your Chats</h1>
          <p className="text-gray-600">Manage all your customer conversations in one place</p>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && rooms.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              ></path>
            </svg>
            <p className="text-gray-500 text-lg">No active chats</p>
            <p className="text-gray-400 mt-2">Chat rooms will appear here when customers message you</p>
          </div>
        )}

        {!loading && !error && rooms.length > 0 && (
          <div className="grid gap-4">
            {rooms.map((r) => (
              <Card key={r.orderId} className="hover:shadow-md transition-shadow duration-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="mb-4 sm:mb-0">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                      <h3 className="font-medium text-gray-800">Order #{String(r.orderId).slice(-6).toUpperCase()}</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      <span className="font-medium">Customer:</span> {r.customer.username || r.customer.email}
                    </p>
                    {r.lastMessage && (
                      <p className="text-gray-500 text-sm mt-2 truncate max-w-md">
                        <span className="italic">Last message:</span> "{r.lastMessage.text}"
                      </p>
                    )}
                    {r.lastMessageTime && (
                      <p className="text-gray-400 text-xs mt-1">{new Date(r.lastMessageTime).toLocaleString()}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors duration-200 flex items-center"
                      onClick={() => navigate(`/chat/${r.orderId}`)}
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        ></path>
                      </svg>
                      Chat
                    </button>
                    <button
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center"
                      onClick={() => navigate(`/owner/order-management/${r.orderId}`)}
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        ></path>
                      </svg>
                      Order
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardTemplate>
  )
}
